import { useEffect, useState } from "react";
import { AdminDatePicker } from "@/components/admin/AdminDatePicker";
import { AdminMenuSelect } from "@/components/admin/AdminMenuSelect";
import { SwitchControl, TextArea, TextInput } from "../_components/AdminShell";
import {
  displayTimeToHm,
  isoDatePart,
  isoToDisplayTime,
  maskDisplayTime,
} from "../../../lib/dates/isoDate";
import {
  isValidDateSubmit,
  normalizeDateTimeSubmit,
  parseCalendarDate,
} from "../../../lib/forms/dateField";
import {
  isDisplayFieldType,
  labelForApiFieldType,
} from "../../../lib/formBuilder/apiFieldTypes";
import { resolveFieldType } from "../../../lib/forms/normalizeFieldType";
import {
  VISIBILITY_OPERATOR_OPTIONS,
  displayCityVisibilityValue,
  firstAllowedVisibilityOperator,
  formatVisibilitySummary,
  isDateLikeFieldType,
  visibilityOperatorAllowedForType,
  visibilityOperatorForUi,
  visibilityOperatorNeedsValue,
} from "../../../lib/formBuilder/visibilityOperators";
import type { FormBuilderField } from "../../../types/formBuilder";
import {
  VisibilityCityValue,
  useCityNameById,
} from "./VisibilityCityValue";
import type {
  VisibilityAction,
  VisibilityOperator,
} from "../../../types/visibility";

export function fieldVisibilityCaption(field: {
  name: string;
  label: string;
}): string {
  const label = field.label.trim();
  if (label && label !== field.name) return label;
  return field.name || "Без названия";
}

export function VisibilityConditionEditor({
  field,
  otherFields,
  onChange,
}: {
  field: FormBuilderField;
  otherFields: FormBuilderField[];
  onChange: (patch: Partial<FormBuilderField>) => void;
}) {
  const operator = visibilityOperatorForUi(
    field.visibilityOperator || "equals",
  );
  const needsValue = visibilityOperatorNeedsValue(operator);
  const selected =
    otherFields.find((item) => item.name === field.visibilityField) ?? null;
  const citySource = resolveFieldType(selected?.type) === "city";
  const cityNames = useCityNameById(citySource);
  const summary = formatVisibilitySummary({
    action: field.visibilityAction || "show",
    fieldLabel: selected
      ? fieldVisibilityCaption(selected)
      : field.visibilityField,
    operator,
    value: citySource
      ? displayCityVisibilityValue(field.visibilityValue, cityNames)
      : field.visibilityValue,
  });

  const operatorOptions = VISIBILITY_OPERATOR_OPTIONS.map((option) => {
    const allowed = visibilityOperatorAllowedForType(
      option.value,
      selected?.type,
    );
    return {
      value: option.value,
      label: option.label,
      icon: <OperatorMark symbol={option.symbol} />,
      disabled: Boolean(selected) && !allowed,
      hint:
        selected && !allowed
          ? `Не подходит для «${labelForApiFieldType(selected.type)}»`
          : undefined,
    };
  });

  const fieldOptions = otherFields.map((item) => ({
    value: item.name,
    label: fieldVisibilityCaption(item),
    description: labelForApiFieldType(item.type),
    disabled: isDisplayFieldType(item.type),
    hint: isDisplayFieldType(item.type)
      ? "У оформления нет значения для сравнения"
      : undefined,
  }));

  const selectField = (visibilityField: string) => {
    const next = otherFields.find((item) => item.name === visibilityField);
    const nextOperator = firstAllowedVisibilityOperator(next?.type, operator);
    const nextIsCity = resolveFieldType(next?.type) === "city";
    const keepValue =
      nextIsCity === citySource &&
      (!isDateLikeFieldType(next?.type) ||
        Boolean(parseCalendarDate(field.visibilityValue)));
    onChange({
      visibilityField,
      visibilityOperator: nextOperator,
      visibilityValue: keepValue ? field.visibilityValue : "",
    });
  };

  const dateSource = isDateLikeFieldType(selected?.type);
  const dateWithTime = Boolean(
    dateSource && selected?.type === "date" && selected.withTime,
  );
  const dateValueInvalid =
    dateSource &&
    needsValue &&
    field.visibilityValue.trim() !== "" &&
    !isValidDateSubmit(field.visibilityValue, dateWithTime);

  return (
    <div className="space-y-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-onest text-sm font-medium text-slate-800">
            Зависит от другого поля
          </p>
          <p className="mt-0.5 font-onest text-[11px] leading-snug text-slate-500">
            Показать или скрыть это поле по значению соседнего
          </p>
        </div>
        <SwitchControl
          id={`visibility-${field.clientId}`}
          compact
          checked={field.visibilityEnabled}
          ariaLabel="Зависит от другого поля"
          checkedClassName="bg-brand-500"
          uncheckedClassName="bg-slate-300"
          onChange={(visibilityEnabled) => {
            const source =
              field.visibilityField.trim() || otherFields[0]?.name || "";
            const sourceField = otherFields.find(
              (item) => item.name === source,
            );
            onChange({
              visibilityEnabled,
              visibilityField: source,
              visibilityOperator: firstAllowedVisibilityOperator(
                sourceField?.type,
                operator,
              ),
            });
          }}
        />
      </div>

      {field.visibilityEnabled ? (
        otherFields.length === 0 ? (
          <p className="rounded-lg bg-slate-50 px-2.5 py-2 font-onest text-[11px] leading-snug text-slate-500">
            Добавьте ещё одно поле — от него можно будет зависеть.
          </p>
        ) : (
          <div className="space-y-2 rounded-lg bg-slate-50 p-2.5">
            <div className="grid grid-cols-2 gap-1 rounded-lg bg-white p-0.5 ring-1 ring-slate-200">
              <ActionOption
                active={(field.visibilityAction || "show") === "show"}
                onClick={() => onChange({ visibilityAction: "show" })}
              >
                Показать
              </ActionOption>
              <ActionOption
                active={field.visibilityAction === "hide"}
                onClick={() => onChange({ visibilityAction: "hide" })}
              >
                Скрыть
              </ActionOption>
            </div>

            <p className="font-onest text-[11px] text-slate-500">
              это поле, если
            </p>

            <AdminMenuSelect
              ariaLabel="Поле условия"
              value={field.visibilityField}
              placeholder="Выберите поле"
              options={fieldOptions}
              onChange={selectField}
            />

            <div className="space-y-1.5">
              <AdminMenuSelect
                ariaLabel="Оператор"
                value={operator}
                options={operatorOptions}
                onChange={(visibilityOperator) =>
                  onChange({
                    visibilityOperator:
                      visibilityOperator as VisibilityOperator,
                    visibilityValue:
                      dateSource && !parseCalendarDate(field.visibilityValue)
                        ? ""
                        : field.visibilityValue,
                  })
                }
              />
              {needsValue && operator !== "in" ? (
                dateSource ? (
                  <VisibilityDateValue
                    value={field.visibilityValue}
                    withTime={dateWithTime}
                    onChange={(visibilityValue) =>
                      onChange({ visibilityValue })
                    }
                  />
                ) : citySource ? (
                  <VisibilityCityValue
                    value={field.visibilityValue}
                    multiple={false}
                    names={cityNames}
                    onChange={(visibilityValue) =>
                      onChange({ visibilityValue })
                    }
                  />
                ) : (
                  <TextInput
                    size="compact"
                    value={field.visibilityValue}
                    placeholder={
                      operator === "gt" ||
                      operator === "gte" ||
                      operator === "lt" ||
                      operator === "lte"
                        ? "число"
                        : "значение"
                    }
                    onChange={(visibilityValue) =>
                      onChange({ visibilityValue })
                    }
                  />
                )
              ) : null}
              {dateValueInvalid ? (
                <p className="font-onest text-[11px] text-red-600">
                  {dateWithTime
                    ? "Укажите дату и время"
                    : "Укажите дату, не число"}
                </p>
              ) : null}
            </div>

            {needsValue && operator === "in" ? (
              citySource ? (
                <VisibilityCityValue
                  value={field.visibilityValue}
                  multiple
                  names={cityNames}
                  onChange={(visibilityValue) => onChange({ visibilityValue })}
                />
              ) : (
                <TextArea
                  size="compact"
                  rows={2}
                  value={field.visibilityValue}
                  placeholder={"msk\nspb\nkzn"}
                  onChange={(visibilityValue) => onChange({ visibilityValue })}
                />
              )
            ) : null}

            {operator === "in" && !citySource ? (
              <p className="font-onest text-[10px] leading-snug text-slate-400">
                По одному значению на строку или через запятую
              </p>
            ) : null}

            <p className="font-onest text-[11px] leading-snug text-slate-600">
              {summary}
            </p>
          </div>
        )
      ) : null}
    </div>
  );
}

function VisibilityDateValue({
  value,
  withTime,
  onChange,
}: {
  value: string;
  withTime: boolean;
  onChange: (value: string) => void;
}) {
  const dateIso = parseCalendarDate(value) ? isoDatePart(value) : "";
  const [timeDraft, setTimeDraft] = useState(() =>
    withTime && value ? isoToDisplayTime(value) : "",
  );

  useEffect(() => {
    setTimeDraft(withTime && value ? isoToDisplayTime(value) : "");
  }, [value, withTime]);

  const commit = (isoDate: string, time: string) => {
    if (!isoDate) {
      onChange("");
      return;
    }
    if (!withTime) {
      onChange(isoDate);
      return;
    }
    const hm = displayTimeToHm(time) ? time : "00:00";
    onChange(normalizeDateTimeSubmit(`${isoDate}T${hm}`));
  };

  return (
    <div className="flex w-full items-center gap-1.5">
      <AdminDatePicker
        compact
        calendarLabel="Выбрать дату"
        value={dateIso}
        onChange={(iso) => commit(iso, timeDraft)}
      />
      {withTime ? (
        <input
          value={timeDraft}
          inputMode="numeric"
          autoComplete="off"
          placeholder="ЧЧ:ММ"
          aria-label="Время"
          onChange={(event) => {
            const next = maskDisplayTime(event.target.value);
            setTimeDraft(next);
            if (!dateIso) return;
            if (!next) {
              onChange(dateIso);
              return;
            }
            if (displayTimeToHm(next)) commit(dateIso, next);
          }}
          className="w-[4.75rem] shrink-0 rounded-md border border-slate-200 bg-white px-2 py-1.5 font-onest text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
        />
      ) : null}
    </div>
  );
}

function OperatorMark({ symbol }: { symbol: string }) {
  return <span className="font-mono leading-none">{symbol}</span>;
}

function ActionOption({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-md px-2 py-1.5 font-onest text-xs font-medium transition-colors",
        active
          ? "bg-brand-500 text-white shadow-sm"
          : "text-slate-500 hover:bg-slate-50 hover:text-slate-700",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

export function visibilitySummaryForField(
  field: FormBuilderField,
  otherFields: FormBuilderField[],
  cityNames: Record<string, string> = {},
): string {
  const selected = otherFields.find(
    (item) => item.name === field.visibilityField,
  );
  const citySource = resolveFieldType(selected?.type) === "city";
  return formatVisibilitySummary({
    action: (field.visibilityAction || "show") as VisibilityAction,
    fieldLabel: selected
      ? fieldVisibilityCaption(selected)
      : field.visibilityField,
    operator: visibilityOperatorForUi(field.visibilityOperator || "equals"),
    value: citySource
      ? displayCityVisibilityValue(field.visibilityValue, cityNames)
      : field.visibilityValue,
  });
}
